import React, { useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AlertCircle, ArrowLeft, ArrowRight, CheckCircle2, Lock, Mail, User } from "lucide-react";
import { useAuth } from "@/features/auth/AuthContext";
import { clearPendingSignupRecovery, rememberPendingSignupRecovery } from "./billingSetupRecovery";
import { log } from "@/shared/utils/logger";
import { useSettings } from "@/features/settings/contexts/SettingsContext";
import { AuthErrorSummary } from "./components/AuthErrorSummary";
import { AuthenticatedAuthNotice } from "./components/AuthenticatedAuthNotice";
import { AuthEntryShell, type SignupPlanId } from "./components/AuthEntryShell";
import { LoginFormInput } from "./components/LoginFormInput";
import { cn, useTheme } from "@/ui/theme/ThemeContext";

type SignupFieldErrors = {
  fullName?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
};

type SignupStep = "account" | "plan";

const splitFullName = (fullName: string) => {
  const parts = fullName.trim().split(/\s+/).filter(Boolean);
  if (parts.length <= 1) {
    return { firstName: parts[0], lastName: undefined };
  }
  return {
    firstName: parts[0],
    lastName: parts.slice(1).join(" "),
  };
};

const normalizeSignupPlan = (rawPlan: string | null): SignupPlanId => {
  if (rawPlan === "free" || rawPlan === "pro" || rawPlan === "ultra") {
    return rawPlan;
  }

  return "pro";
};

const primaryActionClass =
  "inline-flex h-11 min-h-[44px] items-center justify-center gap-2 rounded-md bg-[linear-gradient(135deg,#2563eb,#06b6d4_55%,#10b981)] px-4 py-2 font-semibold text-white shadow-[0_14px_34px_rgba(37,99,235,0.34)] transition-all duration-300 hover:shadow-[0_18px_44px_rgba(6,182,212,0.34)] focus:outline-none focus:ring-2 focus:ring-blue-400/60 focus:ring-offset-2 focus:ring-offset-white disabled:cursor-not-allowed disabled:opacity-70 dark:focus:ring-cyan-300/60 dark:focus:ring-offset-zinc-950";

const secondaryActionClass =
  "inline-flex h-11 min-h-[44px] items-center justify-center gap-2 rounded-md border border-zinc-300/80 bg-white/70 px-4 py-2 text-sm font-semibold text-zinc-950 transition-all duration-300 hover:border-zinc-400 hover:bg-white focus:outline-none focus:ring-2 focus:ring-blue-400/60 focus:ring-offset-2 focus:ring-offset-white dark:border-white/[0.14] dark:bg-white/[0.05] dark:text-white dark:hover:border-white/25 dark:hover:bg-white/[0.09] dark:focus:ring-cyan-300/60 dark:focus:ring-offset-zinc-950";

export const SignupPage: React.FC = () => {
  const { t } = useSettings();
  const { theme: appTheme } = useTheme();
  const { signup, logout, isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const errorSummaryRef = useRef<HTMLDivElement | null>(null);
  const serverErrorRef = useRef<HTMLDivElement | null>(null);

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState<SignupPlanId>(() => {
    const params = new URLSearchParams(location.search);
    return normalizeSignupPlan(params.get("plan"));
  });
  const [signupStep, setSignupStep] = useState<SignupStep>("account");
  const [fieldErrors, setFieldErrors] = useState<SignupFieldErrors>({});
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const returnPath = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const rawReturnTo = params.get("returnTo") || "/";
    return rawReturnTo.startsWith("/") && !rawReturnTo.startsWith("//") ? rawReturnTo : "/";
  }, [location.search]);

  const loginPath = useMemo(
    () => `/login?returnTo=${encodeURIComponent(returnPath)}`,
    [returnPath]
  );

  const summaryErrors = useMemo(
    () =>
      [
        fieldErrors.fullName ? { id: "signup-full-name", message: fieldErrors.fullName } : null,
        fieldErrors.email ? { id: "signup-email", message: fieldErrors.email } : null,
        fieldErrors.password ? { id: "signup-password", message: fieldErrors.password } : null,
        fieldErrors.confirmPassword
          ? { id: "signup-confirm-password", message: fieldErrors.confirmPassword }
          : null,
      ].filter(Boolean) as Array<{ id: string; message: string }>,
    [fieldErrors]
  );

  const validateFields = () => {
    const nextErrors: SignupFieldErrors = {};
    const cleanEmail = email.trim();

    if (!fullName.trim()) {
      nextErrors.fullName = t("Enter your name");
    }

    if (!cleanEmail) {
      nextErrors.email = t("Enter your email");
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleanEmail)) {
      nextErrors.email = t("Enter a valid email");
    }

    if (!password.trim()) {
      nextErrors.password = t("Create a password");
    } else if (password.length < 8) {
      nextErrors.password = t("Use at least 8 characters");
    }

    if (!confirmPassword) {
      nextErrors.confirmPassword = t("Confirm your password");
    } else if (confirmPassword !== password) {
      nextErrors.confirmPassword = t("Passwords do not match");
    }

    return nextErrors;
  };

  const getFriendlySignupError = (message: string) => {
    const normalized = message.trim().toLowerCase();

    if (normalized.includes("already exists")) {
      return t("An account with that email already exists. Sign in or use a different work email.");
    }

    return message;
  };

  const clearFieldError = (field: keyof SignupFieldErrors, hasValue: boolean) => {
    if (!hasValue) return;
    setFieldErrors((current) => ({
      ...current,
      [field]: undefined,
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError("");
    const nextErrors = validateFields();
    setFieldErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      window.requestAnimationFrame(() => {
        errorSummaryRef.current?.focus();
      });
      return;
    }

    if (signupStep === "account") {
      setSignupStep("plan");
      return;
    }

    const nameParts = splitFullName(fullName);
    log.info("SignupPage", "Signup request:", {
      email: email.trim(),
      rememberMe,
    });

    setIsSubmitting(true);
    try {
      const response = await signup({
        email: email.trim().toLowerCase(),
        password,
        firstName: nameParts.firstName,
        lastName: nameParts.lastName,
        selectedPlan,
        rememberMe,
      });

      if (selectedPlan !== "free") {
        rememberPendingSignupRecovery(response.user.id, selectedPlan, returnPath);
      } else {
        clearPendingSignupRecovery();
      }

      if (selectedPlan !== "free") {
        navigate(
          `/billing/setup?plan=${selectedPlan}&returnTo=${encodeURIComponent(returnPath)}`,
          { replace: true }
        );
        return;
      }

      navigate(returnPath, { replace: true });
    } catch (err) {
      setError(
        err instanceof Error
          ? getFriendlySignupError(err.message)
          : t("Failed to create account. Please try again.")
      );
      // Announce and focus the server-side failure the same way field errors are.
      window.requestAnimationFrame(() => {
        serverErrorRef.current?.focus();
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAuthenticatedLogout = async () => {
    await logout();
    clearPendingSignupRecovery();
    setFullName("");
    setEmail("");
    setPassword("");
    setConfirmPassword("");
  };

  if (!isLoading && isAuthenticated) {
    return (
      <AuthenticatedAuthNotice
        activeView="signup"
        theme={appTheme}
        title={t("Already signed in")}
        description={t("Continue to Management Tool or sign out to create a different account.")}
        continueLabel={t("Continue to app")}
        signOutLabel={t("Sign out")}
        onContinue={() => {
          clearPendingSignupRecovery();
          navigate(returnPath, { replace: true });
        }}
        onSignOut={() => void handleAuthenticatedLogout()}
      />
    );
  }

  return (
    <AuthEntryShell
      activeView="signup"
      dataAppReady={!isLoading && !isSubmitting}
      panelTitle={signupStep === "plan" ? t("Confirm your plan") : t("Create your Management Tool account")}
      panelDescription={
        signupStep === "plan"
          ? t("Free, Pro, and Ultra are self-service plans. No card is collected, checkout is off, and Enterprise is contact sales.")
          : t("Enter your account details first. You will choose a plan next.")
      }
      switchPrompt={t("Already have an account?")}
      switchLabel={t("Sign in")}
      switchTo={loginPath}
      selectedPlan={selectedPlan}
      onSelectedPlanChange={setSelectedPlan}
      showPlanSelector={signupStep === "plan"}
    >
      <form onSubmit={handleSubmit} className="min-w-0 space-y-5" noValidate>
        {summaryErrors.length > 0 && (
          <AuthErrorSummary
            ref={errorSummaryRef}
            titleId="signup-error-summary-title"
            title={t("There is a problem")}
            items={summaryErrors}
          />
        )}

        {error && (
          <div
            ref={serverErrorRef}
            role="alert"
            tabIndex={-1}
            className="flex items-center gap-2 rounded-md bg-red-500/10 p-3 text-red-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500/60 dark:text-red-400"
          >
            <AlertCircle className="h-4 w-4 shrink-0" />
            <span className="text-sm">{error}</span>
          </div>
        )}

        {signupStep === "account" ? (
          <>
        <LoginFormInput
          id="signup-email"
          type="text"
          label={t("Work email")}
          value={email}
          onChange={(value) => {
            setEmail(value);
            clearFieldError("email", Boolean(value.trim()));
          }}
          placeholder={t("you@company.com")}
          icon={Mail}
          dir="auto"
          autoFocus
          autoComplete="email"
          error={fieldErrors.email}
          describedBy={fieldErrors.email ? "signup-email-error" : undefined}
        />

        <LoginFormInput
          id="signup-full-name"
          type="text"
          label={t("Full name")}
          value={fullName}
          onChange={(value) => {
            setFullName(value);
            clearFieldError("fullName", Boolean(value.trim()));
          }}
          placeholder={t("Ada Lovelace")}
          icon={User}
          dir="auto"
          autoComplete="name"
          error={fieldErrors.fullName}
          describedBy={fieldErrors.fullName ? "signup-full-name-error" : undefined}
        />

        <LoginFormInput
          id="signup-password"
          type="password"
          label={t("Password")}
          value={password}
          onChange={(value) => {
            setPassword(value);
            clearFieldError("password", Boolean(value.trim()) && value.length >= 8);
          }}
          placeholder={t("Create a password")}
          icon={Lock}
          autoComplete="new-password"
          error={fieldErrors.password}
          describedBy={fieldErrors.password ? "signup-password-error" : undefined}
          helperText={t("Use at least 8 characters.")}
        />

        <LoginFormInput
          id="signup-confirm-password"
          type="password"
          label={t("Confirm password")}
          value={confirmPassword}
          onChange={(value) => {
            setConfirmPassword(value);
            clearFieldError("confirmPassword", Boolean(value && value === password));
          }}
          placeholder={t("Repeat password")}
          icon={Lock}
          autoComplete="new-password"
          error={fieldErrors.confirmPassword}
          describedBy={fieldErrors.confirmPassword ? "signup-confirm-password-error" : undefined}
        />

        <label
          htmlFor="signup-remember-me"
          className={cn("inline-flex cursor-pointer items-center gap-2 text-xs", appTheme.textSecondary)}
        >
          <input
            id="signup-remember-me"
            type="checkbox"
            checked={rememberMe}
            onChange={(event) => setRememberMe(event.target.checked)}
            aria-label={t("Keep me signed in")}
            className="h-4 w-4 shrink-0 rounded border-gray-300 accent-blue-600 focus:ring-2 focus:ring-zinc-500 dark:accent-notion-accent-blue"
          />
          <span>{t("Keep me signed in")}</span>
        </label>

        <button
          type="submit"
          disabled={isSubmitting}
          className={cn("w-full", primaryActionClass)}
        >
          {t("Continue")}
        </button>
          </>
        ) : (
          <div className="min-w-0 space-y-4">
            <div className="rounded-lg border border-emerald-300/50 bg-emerald-100/70 p-4 shadow-[0_14px_38px_rgba(16,185,129,0.10)] backdrop-blur-xl dark:border-emerald-300/20 dark:bg-emerald-300/10">
              <div className="flex min-w-0 items-start gap-3">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[linear-gradient(135deg,#2563eb,#06b6d4_55%,#10b981)] text-white shadow-[0_10px_28px_rgba(37,99,235,0.18)]" aria-hidden="true">
                  <CheckCircle2 className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-zinc-950 dark:text-white">
                    {t("Account details ready")}
                  </p>
                  <p className="mt-1 break-words text-sm font-medium text-emerald-900 dark:text-emerald-100">
                    {email.trim().toLowerCase()}
                  </p>
                  <p className="mt-2 text-xs leading-5 text-zinc-700 dark:text-zinc-300">
                    {t("Confirm your plan selection. You can upgrade or downgrade later. Enterprise is contact-sales only and does not create users here.")}
                  </p>
                </div>
              </div>
            </div>

            <div className="grid gap-3 sm:grid-cols-[auto_1fr]">
              <button
                type="button"
                onClick={() => setSignupStep("account")}
                className={secondaryActionClass}
              >
                <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                {t("Back")}
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className={cn("w-full", primaryActionClass)}
              >
                {isSubmitting ? t("Creating account...") : t("Create account")}
                {!isSubmitting && <ArrowRight className="h-4 w-4" aria-hidden="true" />}
              </button>
            </div>
          </div>
        )}
      </form>
    </AuthEntryShell>
  );
};
